import { logger } from '@/core/logging/logger'

/**
 * Service worker registration.
 *
 * Deliberately does NOT auto-reload on update. Silently swapping the app out
 * mid-edit loses the current selection and is disorienting -- 04-architecture.md
 * section 6. The caller shows a prompt and the user decides when to reload.
 *
 * The `virtual:pwa-register` module is generated by vite-plugin-pwa and only
 * exists in a production build; with devOptions disabled it resolves to nothing
 * in dev. A STATIC import of it therefore makes this whole module serve empty
 * during `npm run dev`, so `setupServiceWorker` is undefined, calling it throws,
 * and the app renders a blank page.
 *
 * Hence the dynamic import inside the production-only branch: dev never touches
 * the virtual module at all.
 */

export interface SWCallbacks {
  onUpdateAvailable: (reload: () => void) => void
  onOfflineReady?: () => void
}

export function setupServiceWorker({ onUpdateAvailable, onOfflineReady }: SWCallbacks): void {
  if (import.meta.env.DEV) {
    logger.debug('pwa', 'Service worker disabled in dev')
    return
  }

  if (!('serviceWorker' in navigator)) {
    logger.warn('pwa', 'Service workers unsupported; the app will not work offline')
    return
  }

  void import('virtual:pwa-register')
    .then(({ registerSW }) => {
      const updateSW = registerSW({
        immediate: true,

        onNeedRefresh() {
          logger.info('pwa', 'Update available')
          onUpdateAvailable(() => {
            void updateSW(true)
          })
        },

        onOfflineReady() {
          logger.info('pwa', 'Ready to work offline')
          onOfflineReady?.()
        },

        onRegisterError(error: unknown) {
          logger.error('pwa', 'Service worker registration failed', error)
        },
      })
    })
    .catch((err: unknown) => {
      // A failed registration must never take the app down with it -- the app
      // works fine online without a service worker.
      logger.error('pwa', 'Could not load the service worker registration module', err)
    })
}
